import { ICriticalError } from "@crunchit/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { IOrderDto } from "models/order";
import moment from "moment";
import OrderService from "services/OrderService";
import ProductionUnitService from "services/ProductionUnitService";
import { RootState } from "store/store";
import { productionFacilityName } from "utils/constants/storage";
import { formatDate } from "utils/helpers/date";

type Rejection = { rejectValue: ICriticalError };
type LoadOrdersSuccess = { orders: IOrderDto[] };

type ProductionFacility = "finsensvej" | "solrød";
const productionFacilities: Array<{ productionFacility: ProductionFacility; productionUnitIds: number[] }> = [
  {
    productionFacility: "finsensvej",
    productionUnitIds: [326, 327, 328, 329, 330, 331, 332, 333, 334, 335, 336, 365, 404],
  },
  {
    productionFacility: "solrød",
    productionUnitIds: [441],
  },
];

const filterOrderedAfter = (date: moment.Moment, order: IOrderDto) => moment(order.orderTimeUtc).isBefore(date);
const sortByReadyTime = (orderA: IOrderDto, orderB: IOrderDto) => new Date(orderB.orderReadyTime).getTime() - new Date(orderA.orderReadyTime).getTime();

function getLatestOrderingDateUtc(localNow: moment.Moment, chosenDate: moment.Moment) {
  const chosenDateUtc = chosenDate.utc(); // Will be the previous day at 22:00 or 23:00, since chosen date is at 00:00 local Danish time

  const localHours = localNow.hours();
  const localMinutes = localNow.minutes();

  const utcNow = moment.utc();
  utcNow.subtract(localHours, "hours");
  utcNow.subtract(localMinutes, "minutes");

  utcNow.year(chosenDateUtc.year());
  utcNow.date(chosenDateUtc.date());
  utcNow.month(chosenDate.month());
  utcNow.seconds(0);

  return utcNow;
}

export const loadOrders = createAsyncThunk<LoadOrdersSuccess, void, Rejection>("order/loadOrders", async (data, { getState, rejectWithValue }) => {
  const state = getState() as RootState;

  try {
    const productionFacility: ProductionFacility = (sessionStorage.getItem(productionFacilityName) as ProductionFacility | undefined) || "finsensvej";
    const chosenDate = moment(state.order.chosenDate);
    const date = formatDate(chosenDate);

    const ordersResponse = await OrderService.getOrders({ date });

    if (!ordersResponse.isSuccess()) {
      return rejectWithValue({ message: `Unable to fetch orders'`, error: ordersResponse.errors });
    }

    const ordersResponseData = ordersResponse.data.filter((order) => {
      const productionUnitId = order.productionUnitId;
      const facility = productionFacilities.find((facility) => facility.productionFacility === productionFacility);
      return facility ? facility.productionUnitIds.includes(productionUnitId) : false;
    });

    if (!ordersResponseData.length) {
      return { orders: [] };
    }

    const productionUnitId = ordersResponseData.at(0)!!.productionUnitId;
    const productionUnitNowResponse = await ProductionUnitService.getNow(productionUnitId);

    if (!productionUnitNowResponse.isSuccess()) {
      return rejectWithValue({ message: `Unable to fetch orders'`, error: productionUnitNowResponse.errors });
    }

    const localNow = moment(productionUnitNowResponse.data);
    const latestOrderDateUtc = getLatestOrderingDateUtc(localNow, chosenDate);

    const orders = ordersResponseData.filter((order) => filterOrderedAfter(latestOrderDateUtc, order)).sort(sortByReadyTime);

    return { orders };
  } catch (error) {
    return rejectWithValue({ message: "Unable to initialize app", error: error instanceof Error ? error.message : error });
  }
});

const thunks = {
  loadOrders,
};

export default thunks;
